import { db, auth, timestamp, firebase, FieldValue } from '@/firebaseConfig'
import router from '@/router'
import { goToPath } from '@/mixins'
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth'

export default {
  state: {
    user: null,
    contactDetails: [],
    actionCode: null,
    userEmail: null
  },
  mutations: {
    setUser(state, payload) {
      state.user = payload
    },
    createContact(state, payload) {
      state.contactDetails.push(payload)
    },
    setActionCode(state, payload) {
      state.actionCode = payload
    }
  },
  actions: {
    async signUserUp({ commit }, payload) {
      commit('clearError')

      /** create customer */
      const newCustomer = await auth.createUserWithEmailAndPassword(payload.email, payload.password)

      const customerDetails = {
        id: newCustomer.user.uid,
        email: payload.email,
        firstName: payload.firstName,
        lastName: payload.lastName,
        isAccountConfirmed: false,
        isAccountApproved: false,
        type: 'customer',
        createdAt: timestamp,
        updatedAt: timestamp,
        created_at_mills: '',
        creationError: ''
      }

      const fieldsForSorting = {
        created_at_mills: ''
      }

      /** store customer details to firestore  */
      await db.doc(`/customers/${customerDetails.id}/customerData/details`).set(customerDetails)

      /** add an array to store ids of favoriteProductsIds */
      await db.doc(`/customers/${customerDetails.id}/customerData/favoriteProductsIds`).set({ ids: [] }, { merge: true })

      /** add sort field to the customer */
      await db.doc(`/customers/${customerDetails.id}`).set(fieldsForSorting)

      /** store customer info in local state */
      commit('setUser', customerDetails)

      /** redirect user to the welcome page */
      goToPath('/welcome')
    },

    signUserIn({ commit, dispatch }, payload) {
      return new Promise((resolve, reject) => {
        commit('setLoading', true)
        commit('clearError')
        auth.signInWithEmailAndPassword(payload.email, payload.password)
          .then((snap) => {
            const user = snap.user
            switch (user.displayName) {
              case 'customer':
                dispatch('fetchCustomer', user)
                break
              case 'business':
                dispatch('fetchBusSupplier', user)
                break
              case 'independent':
                dispatch('fetchIndSupplier', user)
                break
              case 'admin':
                dispatch('fetchAdminData', user)
                break
              default:
                break
            }
            commit('setLoading', false)
            resolve(user.displayName)
          })
          .catch((error) => {
            commit('setLoading', false)
            commit('setError', error)
            reject(error)
          })
      })
    },

    async signUserInWithGoogle({ commit }) {
      commit('setLoading', false)
      commit('clearError')
      // const provider = new firebase.auth.GoogleAuthProvider()
      try {
        // const newCustomer = await auth.signInWithPopup(provider)
        const googleUser = await GoogleAuth.signIn()
        const credential = await firebase.auth.GoogleAuthProvider.credential(googleUser.authentication.idToken)
        const newCustomer = await auth.signInWithCredential(credential)

        const customerDetails = {
          id: newCustomer.user.uid,
          email: newCustomer.user.email,
          firstName: newCustomer.additionalUserInfo.profile.given_name,
          lastName: newCustomer.additionalUserInfo.profile.family_name,
          isAccountConfirmed: true,
          isAccountApproved: false,
          type: 'customer',
          createdAt: timestamp,
          updatedAt: timestamp,
          created_at_mills: '',
          creationError: ''
        }

        /**
         * if isNewUser, create firestore record
         */
        if (newCustomer.additionalUserInfo.isNewUser) {
          const fieldsForSorting = {
            created_at_mills: ''
          }
          try {
            /** store customer details to firestore  */
            await db.doc(`/customers/${customerDetails.id}/customerData/details`).set(customerDetails)

            /** add an array to store ids of favoriteProductsIds */
            await db.doc(`/customers/${customerDetails.id}/customerData/favoriteProductsIds`).set({ ids: [] }, { merge: true })

            /** add sort field to the customer */
            await db.doc(`/customers/${customerDetails.id}`).set(fieldsForSorting)

            /** store customer info in local state */
          } catch (err) {
            commit('setError', err)
          }
        }

        commit('setUser', customerDetails)

        /** redirect user to the homepage */
        goToPath('/')

        /** trigger the dropdown menu */
      } catch (err) {
        commit('setError', err)
      }
    },

    async fetchCustomer({ commit, dispatch }, user) {
      commit('setLoading', true)
      const customerPath = `customers/${user.uid}/customerData/details`
      try {
        const customerData = await db.doc(customerPath).get()
        commit('setUser', customerData.data())
        dispatch('getFavoriteProductsIds')
        goToPath('/')
      } catch (err) {
        commit('setError', err)
      }
      commit('setLoading', false)
    },

    async sendMessageToSupplier({ commit, getters }, payload) {
      try {
        const user = getters.user

        let messageRef = null
        let contactedProductsIdsRef = null
        switch (user.type) {
          case 'customer':
            messageRef = await db.collection(`/customers/${user.id}/messages`).doc()
            contactedProductsIdsRef = await db.doc(`/customers/${user.id}/messages/contactedProductsIds`)
            break
          case 'independent':
            messageRef = await db.collection(`/independents/${user.id}/messages`).doc()
            contactedProductsIdsRef = await db.doc(`/independents/${user.id}/messages/contactedProductsIds`)
            break
          case 'business':
            messageRef = await db.collection(`/businesses/${user.id}/messages`).doc()
            contactedProductsIdsRef = await db.doc(`/independents/${user.id}/messages/contactedProductsIds`)
            break
          default:
            break
        }

        await messageRef.set({ ...payload, createdAt: timestamp })
          .then(() => {
            // add the productId to the array of contacted suppliers
            contactedProductsIdsRef.update({ ids: FieldValue.arrayUnion(payload.product.id) })
            commit('addToContactedIds', payload.product.id)
          })
          .catch((err) => {
            commit('setError', err)
          })
      } catch (err) {
        commit('setError', err)
      }
    },

    resetUserPassword({ commit }, payload) {
      commit('clearError')
      auth.sendPasswordResetEmail(payload.email)
        .then(() => {
          router.push({
            path: payload.redirect
          })
        })
        .catch(error => {
          commit('setError', error)
        })
    },

    setNewPassword({ commit, getters, dispatch }, payload) {
      // TODO: set the action url in console to https://zambezifarmer.com/action
      return new Promise((resolve, reject) => {
        commit('clearError')
        const newPassword = payload.newPassword
        const actionCode = getters.actionCode
        const email = getters.user.email
        auth.confirmPasswordReset(actionCode, newPassword)
          .then(() => {
            // TODO: sign in the user with new password
            // Fix the snackbar display
            dispatch('signUserIn', { email, password: newPassword })
              .then((displayName) => {
                const target = displayName === 'customer' ? 'home' : 'dashboard'
                commit('setSnackbar', {
                  text: 'Password has been reset successfully',
                  show: true,
                  timeout: 6000,
                  target
                })
              })
          })
          .catch(error => {
            commit('setError', error)
            reject(error)
          })
      })
    },

    authoSignIn({ commit }, payload) {
      commit('setUser', {
        id: payload.uid
      })
    },

    createContactDetails({ commit, getters }, payload) {
      const userId = getters.user.id
      const contact = {
        firstName: payload.firstName,
        lastName: payload.lastName,
        email: payload.email,
        message: payload.message
      }

      db.ref(`/users/${userId}`).child('contact').set(contact)
      commit('createContact', contact)
    },

    logout({ commit }) {
      auth.signOut()
      goToPath('/')
      commit('setUser', null)
      commit('setFavoriteProductsIds', [])
      commit('setFavoriteProducts', [])
    },

    handleVerifyEmail({ commit }, query) {
      auth.applyActionCode(query.oobCode)
        .then(() => {
          /** E-mail address has been verified */
          router.push('/verified')
        })
        .catch(error => commit('setError', error))
    },

    handleResetPassword({ commit }, actionCode) {
      /** verify the password reset code is valid */
      return new Promise((resolve, reject) => {
        auth.verifyPasswordResetCode(actionCode)
          .then((email) => {
            commit('setActionCode', actionCode)
            commit('setUser', { email })
            /** password reset code is valid */
            resolve()
          })
          .catch(err => {
            reject(err)
          })
      })
    }
  },
  getters: {
    user(state) {
      return state.user
    },
    actionCode(state) {
      return state.actionCode
    }
  }
}
